import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Trophy, Gift, Loader2, Zap } from "lucide-react";
import { API } from "@/assets";

const LANES = ["#FF3399", "#00F0FF", "#A855F7", "#00FF66"]; // pink, teal, purple, green
const LANE_KEYS = ["d", "f", "j", "k"];
const BPM = 138;
const SPAWN_MS = 60000 / BPM / 2; // 8th notes
const FALL_MS = 1500;
const HIT_WINDOW = 95; // px tolerance around the hit line
const MAX_MISSES = 5;

export default function PianoScales({ engine, onScoreSubmitted }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | playing | over
  const [hud, setHud] = useState({ score: 0, combo: 0, misses: 0, mult: 1 });
  const [finalScore, setFinalScore] = useState(null);
  const [board, setBoard] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitNote, setSubmitNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const loadBoard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/arcade/leaderboard`);
      if (res.ok) setBoard((await res.json()).leaderboard || []);
    } catch { /* offline-friendly */ }
  }, []);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const endGame = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    engine?.current?.stopStems();
    setPhase("over");
    setFinalScore(stateRef.current.score);
  }, [engine]);

  const startGame = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    engine?.current?.ensureCtx?.();
    engine?.current?.startStems?.();
    stateRef.current = {
      tiles: [], score: 0, combo: 0, best: 0, misses: 0,
      lastSpawn: performance.now() - SPAWN_MS, t0: performance.now(), over: false,
    };
    setHud({ score: 0, combo: 0, misses: 0, mult: 1 });
    setSubmitted(null);
    setSubmitNote("");
    setPhase("playing");

    const laneW = () => canvas.width / 4;
    const hitY = () => canvas.height * 0.84;

    const spawn = (now) => {
      const st = stateRef.current;
      while (now - st.lastSpawn >= SPAWN_MS) {
        st.lastSpawn += SPAWN_MS;
        if (Math.random() < 0.78) {
          const lane = Math.floor(Math.random() * 4);
          st.tiles.push({ lane, born: st.lastSpawn + (st.lastSpawn < now ? SPAWN_MS : 0), hit: false });
          if (Math.random() < 0.14) { // occasional double-crystal
            const l2 = (lane + 1 + Math.floor(Math.random() * 3)) % 4;
            st.tiles.push({ lane: l2, born: st.lastSpawn, hit: false });
          }
        }
      }
    };

    const draw = (now) => {
      const st = stateRef.current;
      if (!st || st.over) return;
      spawn(now);
      const w = canvas.width = canvas.offsetWidth;
      const h = canvas.height = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      // lanes
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.05)";
        ctx.fillRect(i * laneW(), 0, laneW(), h);
      }
      // hit line
      ctx.strokeStyle = "rgba(255,184,0,0.9)";
      ctx.lineWidth = 2;
      ctx.shadowColor = "#FFB800"; ctx.shadowBlur = 12;
      ctx.beginPath(); ctx.moveTo(0, hitY()); ctx.lineTo(w, hitY()); ctx.stroke();
      ctx.shadowBlur = 0;
      // tiles
      st.tiles = st.tiles.filter((t) => !t.gone);
      for (const t of st.tiles) {
        const progress = (now - t.born) / FALL_MS;
        const y = progress * (hitY() + 60) - 50;
        if (!t.hit && y - 26 > hitY() + HIT_WINDOW) {
          t.hit = true; t.gone = true; // missed past the line
          st.combo = 0; st.misses++;
          setHud({ score: st.score, combo: 0, misses: st.misses, mult: 1 });
          if (st.misses >= MAX_MISSES) { st.over = true; endGame(); return; }
          continue;
        }
        if (y < -60) { t.gone = true; continue; }
        const x = t.lane * laneW();
        const c = LANES[t.lane];
        const fade = t.hit ? 0.25 : 1;
        ctx.globalAlpha = fade;
        ctx.shadowColor = c; ctx.shadowBlur = t.hit ? 4 : 16;
        // scale-shaped tile (diamond in a rounded cell)
        ctx.fillStyle = t.hit ? "rgba(255,255,255,0.12)" : c;
        const cw = laneW() - 14, ch = 46;
        const cx = x + 7, cy = y;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(cx, cy, cw, ch, 10); else ctx.rect(cx, cy, cw, ch);
        ctx.fill();
        ctx.fillStyle = "rgba(0,0,0,0.45)";
        ctx.beginPath();
        ctx.moveTo(cx + cw / 2, cy + 8); ctx.lineTo(cx + cw - 12, cy + ch / 2);
        ctx.lineTo(cx + cw / 2, cy + ch - 8); ctx.lineTo(cx + 12, cy + ch / 2);
        ctx.closePath(); ctx.fill();
        ctx.shadowBlur = 0; ctx.globalAlpha = 1;
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
  }, [engine, endGame]);

  const strike = useCallback((lane) => {
    const st = stateRef.current;
    if (!st || st.over || phase !== "playing") return;
    const now = performance.now();
    const canvas = canvasRef.current;
    const hitLine = canvas.height * 0.84;
    let best = null;
    for (const t of st.tiles) {
      if (t.lane !== lane || t.hit) continue;
      const y = ((now - t.born) / FALL_MS) * (hitLine + 60) - 50 + 23;
      const d = Math.abs(y - hitLine);
      if (d <= HIT_WINDOW && (!best || d < best.d)) best = { t, d };
    }
    if (best) {
      best.t.hit = true;
      best.t.gone = true;
      st.combo++;
      st.best = Math.max(st.best, st.combo);
      const mult = 1 + Math.min(3, Math.floor(st.combo / 8));
      const pts = (best.d < 32 ? 100 : 60) * mult;
      st.score += pts;
      setHud({ score: st.score, combo: st.combo, misses: st.misses, mult });
    }
  }, [phase]);

  useEffect(() => {
    const onKey = (e) => {
      const i = LANE_KEYS.indexOf(e.key.toLowerCase());
      if (i >= 0) strike(i);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [strike]);

  useEffect(() => () => { cancelAnimationFrame(rafRef.current); }, []);

  const onCanvasTap = (e) => {
    if (phase !== "playing") return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    strike(Math.min(3, Math.floor((x / rect.width) * 4)));
  };

  const submitScore = async () => {
    if (!name.trim()) { setSubmitNote("Add a player name first."); return; }
    setSubmitting(true);
    setSubmitNote("");
    try {
      const res = await fetch(`${API}/arcade/score`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), score: finalScore, best_combo: stateRef.current?.best || 0 }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSubmitted(data);
      loadBoard();
      onScoreSubmitted?.(data);
    } catch {
      setSubmitNote("Couldn't save your score — try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="piano-scales">
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="relative bg-black/60 border border-fuchsia-500/30 rounded-2xl overflow-hidden" data-testid="piano-arena">
          <canvas ref={canvasRef} className="w-full h-[420px] sm:h-[520px] block touch-none" onPointerDown={onCanvasTap} data-testid="piano-canvas" />
          {phase !== "playing" && (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6" data-testid="piano-overlay">
              {phase === "idle" ? (
                <>
                  <Zap size={30} className="text-yellow-300 mb-3" />
                  <p className="font-display text-white font-bold text-lg">Piano Scales</p>
                  <p className="text-xs text-ice/55 mt-2 max-w-xs">Scales and crystals fall with the beat. Tap the lane (or keys D · F · J · K) when they cross the gold line. Five misses and the serpent wins.</p>
                  <button onClick={startGame} className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-bold text-sm px-8 py-3.5 rounded-full shadow-[0_0_26px_rgba(236,72,153,0.55)] active:scale-95 transition-all" data-testid="piano-start">
                    <Play size={16} /> Start Game
                  </button>
                </>
              ) : (
                <>
                  <Trophy size={30} className="text-yellow-300 mb-3" />
                  <p className="font-display text-white font-bold text-lg" data-testid="piano-final-score">Score: {finalScore}</p>
                  <p className="text-xs text-ice/55 mt-1">Best combo ×{stateRef.current?.best || 0}</p>
                  {finalScore >= 2000 && !submitted ? (
                    <div className="mt-4 w-full max-w-xs space-y-2.5" data-testid="piano-claim">
                      <p className="text-xs text-yellow-200/90 font-mono uppercase tracking-[0.12em]">Prize tier reached — claim it</p>
                      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Player name *" className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-fuchsia-400/70" data-testid="piano-name" />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (for prize delivery)" className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-fuchsia-400/70" data-testid="piano-email" />
                      <button onClick={submitScore} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold text-xs px-5 py-3 rounded-full hover:shadow-[0_0_20px_rgba(255,184,0,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid="piano-submit-score">
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />} Submit Score & Claim
                      </button>
                    </div>
                  ) : submitted ? (
                    <div className="mt-4 text-center" data-testid="piano-submitted">
                      <p className="text-sm text-emerald-300 font-bold">Rank #{submitted.rank} on the board</p>
                      {submitted.prize && <p className="text-xs text-yellow-200 mt-1.5">Prize earned: {submitted.prize} — the label will confirm by email.</p>}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-ice/50">Score 2,000+ to unlock prize tiers.</p>
                  )}
                  {submitNote && <p className="mt-2 text-xs text-red-300" data-testid="piano-submit-note">{submitNote}</p>}
                  <button onClick={startGame} className="mt-5 inline-flex items-center gap-2 border border-fuchsia-500/50 text-fuchsia-300 font-bold text-xs px-6 py-3 rounded-full hover:bg-fuchsia-500 hover:text-white transition-all" data-testid="piano-retry">
                    <Play size={14} /> Play Again
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="piano-hud">
            {[["Score", hud.score], ["Combo", `×${hud.combo}`], ["Misses", `${hud.misses}/${MAX_MISSES}`]].map(([label, val]) => (
              <div key={label} className="bg-black/50 border border-[#26262e] rounded-xl p-3 text-center">
                <p className="font-display text-white font-bold text-lg">{val}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-ice/45">{label}</p>
              </div>
            ))}
          </div>
          {hud.mult > 1 && phase === "playing" && (
            <p className="mb-3 font-mono text-[11px] text-yellow-300 uppercase tracking-[0.15em]" data-testid="piano-mult">Multiplier ×{hud.mult}</p>
          )}
          <div className="bg-black/50 border border-[#26262e] rounded-xl p-4" data-testid="piano-leaderboard">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-300 mb-3">Top Serpents</p>
            {board === null ? (
              <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-fuchsia-400" /></div>
            ) : board.length === 0 ? (
              <p className="text-xs text-ice/40 py-4 text-center">No scores yet — be the first legend.</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {board.map((s, i) => (
                  <div key={s.s_id} className="flex items-center gap-2.5 text-xs" data-testid={`board-${s.s_id}`}>
                    <span className={`w-6 font-mono ${i === 0 ? "text-yellow-300" : i < 3 ? "text-fuchsia-300" : "text-ice/40"}`}>#{i + 1}</span>
                    <span className="text-ice/85 truncate flex-1">{s.name}</span>
                    {s.prize && <Gift size={11} className="text-yellow-300/70 shrink-0" />}
                    <span className="font-mono text-white">{s.score.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="mt-3 text-[10px] text-ice/35 font-mono uppercase tracking-[0.12em]">Prize tiers: 2,000 sample kit · 5,000 merch code · 10,000 free lease</p>
        </div>
      </div>
    </div>
  );
}
